import React, { useState, useEffect } from "react";
import {
  Box,
  Table,
  TableBody,
  TableHead,
  TableRow,
  Typography,
  LinearProgress,
  Menu,
  MenuItem,
  ListItemText,
  ListItemIcon,
  IconButton,
  CircularProgress,
} from "@mui/material";
import {
  NoDataContainer,
  StyledTableCell,
  TableBorderRadiusTopLeftRight,
} from "../../../Common/styles/table";
import { MoreVert } from "@mui/icons-material";
import http from "../../../../utils/http";
import { errorToastMessage, toastMessage } from "../../../../utils/toast";
import { AxiosResponse } from "axios";
import EditPermissionModal from "../../EditPermissionModal";
import { CustomStatusChip } from "../../../Common/UI/StatusTag";

const statusMap: any = {
  draft: { label: "Draft", color: "#111928", bgColor: "#F3F4F6" },
  published: { label: "Published", color: "#03543F", bgColor: "#DEF7EC" },
};

type Props = {
  type: string;
  childTab: string;
  showModal: string;
  setShowModal: Function;
  searchText: string;
  setSearchText: Function;
  canEdit: boolean;
};

const Study = ({
  type,
  childTab,
  showModal,
  searchText,
  setShowModal,
  setSearchText,
  canEdit,
}: Props) => {
  //   const [showFilterModal, setShowFilterModal] = useState<boolean>(false);
  const [permissionData, setPermissionData] = useState<any>([]);
  const [permissionCount, setPermissionCount] = useState<number>(0);
  const [selectedRow, setSelectedRow] = useState<any>(null);

  const [toggleLoader, setToggleLoader] = useState<boolean>(false);
  const [menuLoader, setMenuLoader] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  useEffect(() => {
    const fetchDetails = async () => {
      try {
        setLoading(true);
        let url = `/admin/permission?&type=${childTab}`;
        if (searchText) {
          url += `&search=${searchText}`;
        }

        const res: AxiosResponse = await http.get(url);
        const data = res.data?.data;
        const newData = data?.map((row: any) => ({
          id: row?.id,
          label: row?.label || "-",
          description: row?.description || "-",
          linkedFeature: row?.linkedFeature?.name || "-",
          linkedFeatureId: row?.linkedFeature?.id,
          code: row?.code || "-",
          status: row?.isPublished ? "published" : "draft",
        }));
        setPermissionData(newData || []);
        setPermissionCount(data?.length || 0);
        setLoading(false);
      } catch (err) {
        setLoading(false);
        errorToastMessage(err as Error);
      }
    };
    fetchDetails();
  }, [childTab, toggleLoader, searchText]);

  const refreshPage = () => {
    setToggleLoader((prev) => !prev);
  };

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>, row: any) => {
    setSelectedRow(row);
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedRow(null);
  };

  // const modifySearchTerm = useMemo(
  //   () =>
  //     debounce((val) => {
  //       setSearchText(val);
  //     }, 500),
  //   [setSearchText]
  // );
  //   const openFilterModal = () => {
  //     setShowFilterModal(true);
  //   };
  //   const closeFilterModal = () => {
  //     setShowFilterModal(false);
  //   };

  const openModal = (type: string) => {
    setShowModal(type);
  };

  const closeModal = () => {
    setShowModal("");
    setSelectedRow(null);
  };

  const publishPermission = async () => {
    try {
      setMenuLoader(true);
      const res = await http.post(
        `/admin/permission/${selectedRow?.id}/publish`
      );
      handleMenuClose();
      toastMessage("success", res.data.message);
      setMenuLoader(false);
      refreshPage();
    } catch (err) {
      errorToastMessage(err as Error);
      setMenuLoader(false);
    }
  };

  return (
    <Box>
      <Box sx={{ p: 2, backgroundColor: "#fff", borderRadius: "10px" }}>
        {/* <Box sx={SearchBox}>
          <TextField
            sx={{ width: "70%" }}
            variant="outlined"
            placeholder="Search"
            defaultValue={searchText}
            onChange={(e) => modifySearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: "#637E85" }} />
                </InputAdornment>
              ),
            }}
          />
          <Box sx={{ ...filterBox, width: "30%" }} onClick={openFilterModal}>
            <Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <SortRounded />
              <Typography>Filter</Typography>
            </Box>
            <ExpandMoreRounded />
          </Box>
        </Box> */}
        <Table sx={TableBorderRadiusTopLeftRight}>
          <TableHead>
            <TableRow>
              <StyledTableCell>Label</StyledTableCell>
              <StyledTableCell>Description</StyledTableCell>
              <StyledTableCell>Feature</StyledTableCell>
              <StyledTableCell>Code</StyledTableCell>
              <StyledTableCell>Status</StyledTableCell>
              {canEdit && <StyledTableCell />}
            </TableRow>
          </TableHead>

          {!loading && permissionCount > 0 && (
            <TableBody>
              {permissionData?.map((row: any) => (
                <TableRow key={row?.id}>
                  <StyledTableCell>{row?.label}</StyledTableCell>
                  <StyledTableCell>{row?.description}</StyledTableCell>
                  <StyledTableCell>{row?.linkedFeature}</StyledTableCell>
                  <StyledTableCell>{row?.code}</StyledTableCell>
                  <StyledTableCell
                  // sx={{
                  //   color:
                  //     row?.status === "Published" ? "#31C48D" : "#3F83F8",
                  // }}
                  >
                    <CustomStatusChip
                      label={statusMap[row?.status]?.label}
                      color={statusMap[row?.status]?.color}
                      bgColor={statusMap?.[row.status]?.bgColor}
                    />
                  </StyledTableCell>
                  {canEdit && (
                    <StyledTableCell align="right">
                      <IconButton onClick={(e) => handleMenuClick(e, row)}>
                        <MoreVert />
                      </IconButton>
                    </StyledTableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          )}
        </Table>
        {!loading && permissionCount === 0 && (
          <NoDataContainer>
            <Typography variant="body1" color="gray">
              No Data
            </Typography>
          </NoDataContainer>
        )}
        {loading && (
          <Box sx={{ width: "100%" }}>
            <LinearProgress />
          </Box>
        )}
      </Box>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleMenuClose}
        TransitionProps={{
          exit: false,
        }}
      >
        <MenuItem
          onClick={() => {
            setAnchorEl(null);
            openModal("edit_permission");
          }}
          disabled={menuLoader}
        >
          <ListItemText>Edit</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={publishPermission}
          disabled={menuLoader || selectedRow?.status === "published"}
        >
          <ListItemText>Publish</ListItemText>
          {menuLoader && (
            <ListItemIcon>
              <CircularProgress size={18} sx={{ ml: 1 }} />
            </ListItemIcon>
          )}
        </MenuItem>
      </Menu>

      {/* {showFilterModal && (
        <FeatureFilterModal
          showModal={showFilterModal}
          closeModal={closeFilterModal}
        />
      )} */}

      {showModal === "edit_permission" && (
        <EditPermissionModal
          showModal={showModal === "edit_permission"}
          closeModal={closeModal}
          data={selectedRow}
          refreshPage={refreshPage}
        />
      )}
    </Box>
  );
};

export default Study;
